// Breach Step 7: Notification Requirements
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { DatePicker } from '../../../../components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import {
  AlertTriangle, CheckCircle2, HelpCircle, Info, Gauge, Send, FileText, Users, Shield, PenLine, AlertCircle
} from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { getJurisdictionRules, SUPERVISORY_AUTHORITIES } from '../constants';

export default function Step7Notifications({ formData, updateField, notificationRequirement, user, selectedEntity }) {
  const isDpoRole = ['dpo', 'super_admin', 'org_owner'].includes(user?.role);
  const rules = getJurisdictionRules(selectedEntity?.jurisdiction || selectedEntity?.country) || null;
  const isMandatory = rules ? rules.dpo_discretion === false : false;
  const riskLevel = formData.final_risk_level;
  const requirement = notificationRequirement;

  const suggestedDeadlineDate = formData.awareness_date && rules?.deadlineDays
    ? (() => {
        const d = new Date(formData.awareness_date);
        d.setDate(d.getDate() + rules.deadlineDays);
        return d.toISOString().split('T')[0];
      })()
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Notification Requirements</h2>
        <p className="text-muted-foreground mt-1">
          Review jurisdiction-specific requirements and confirm your notification decision.
        </p>
      </div>

      {/* Jurisdiction not set — hard block */}
      {!rules && formData.entity_id && (
        <Card className="border-2 border-amber-500 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="py-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-300">Jurisdiction not set for this entity</p>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  Jurisdiction determines your reporting obligations and deadline. Please update the entity settings before proceeding.
                </p>
                <a href={`/admin/entities`} className="text-sm text-primary underline mt-2 inline-block">
                  → Go to Entity Settings
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jurisdiction Assessment Card */}
      {rules && (
      <Card className={cn(
        "border-2",
        isMandatory && "border-red-500 bg-red-50/50 dark:bg-red-950/20",
        !isMandatory && requirement?.mustReport === true && "border-red-500 bg-red-50/50 dark:bg-red-950/20",
        !isMandatory && requirement?.mustReport === false && "border-green-500 bg-green-50/50 dark:bg-green-950/20",
        !isMandatory && requirement?.mustReport === null && "border-amber-500 bg-amber-50/50 dark:bg-amber-950/20"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className={cn("h-5 w-5",
                isMandatory ? "text-red-500" :
                requirement?.mustReport === true ? "text-red-500" :
                requirement?.mustReport === false ? "text-green-500" : "text-amber-500"
              )} />
              Jurisdiction Assessment
            </CardTitle>
            {riskLevel && (
              <Badge className={cn(
                riskLevel === 'Critical' && "bg-red-600",
                riskLevel === 'High' && "bg-orange-500",
                riskLevel === 'Medium' && "bg-amber-500",
                riskLevel === 'Low' && "bg-green-500"
              )}>
                {riskLevel} Risk
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!riskLevel && !isMandatory ? (
            <div className="text-center py-4 text-muted-foreground">
              <Gauge className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Complete the Risk Assessment (Step 6) to see notification requirements</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div><p className="text-xs text-muted-foreground">Jurisdiction</p><p className="font-medium">{formData.jurisdiction}</p></div>
                <div>
                  <p className="text-xs text-muted-foreground">Supervisory Authority</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-medium">{rules.authorityName}</p>
                    {rules.regulatorLink && (
                      <a href={rules.regulatorLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">→</a>
                    )}
                  </div>
                </div>
                <div><p className="text-xs text-muted-foreground">Reporting Threshold</p><p className="font-medium">{rules.thresholdDescription}</p></div>
                <div><p className="text-xs text-muted-foreground">Deadline</p><p className="font-medium">{rules.deadlineDays} days from awareness</p></div>
              </div>

              <div className={cn(
                "p-4 rounded-lg border-2",
                (isMandatory || requirement?.mustReport === true) && "border-red-300 bg-red-100 dark:bg-red-900/30 dark:border-red-700",
                !isMandatory && requirement?.mustReport === false && "border-green-300 bg-green-100 dark:bg-green-900/30 dark:border-green-700",
                !isMandatory && requirement?.mustReport === null && "border-amber-300 bg-amber-100 dark:bg-amber-900/30 dark:border-amber-700"
              )}>
                <div className="flex items-start gap-3">
                  {(isMandatory || requirement?.mustReport === true) && <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />}
                  {!isMandatory && requirement?.mustReport === false && <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />}
                  {!isMandatory && requirement?.mustReport === null && <HelpCircle className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />}
                  <div>
                    <p className="font-semibold text-sm">
                      {isMandatory ? 'Notification Mandatory' :
                       requirement?.mustReport === true ? 'Notification Required' :
                       requirement?.mustReport === false ? 'Notification Not Mandatory' :
                       'DPO Decision Required'}
                    </p>
                    <p className="text-sm mt-1">
                      {isMandatory
                        ? `Notification to regulator and data subjects is mandatory for all breaches under ${rules.legalBasis}.`
                        : requirement?.reportReason}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">Legal Basis: {rules.legalBasis}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* DPO Decision Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><PenLine className="h-4 w-4 text-primary" />DPO Reporting Decision</CardTitle>
          <CardDescription>Confirm whether this breach will be reported to the supervisory authority</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isDpoRole ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button type="button" data-testid="dpo-decision-report" onClick={() => updateField('dpo_reporting_decision', 'report')}
                className={cn("p-4 border-2 rounded-xl text-left transition-all relative",
                  formData.dpo_reporting_decision === 'report' ? "border-red-500 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-500" : "border-border hover:border-red-300")}>
                {formData.dpo_reporting_decision === 'report' && <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-red-500" />}
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center"><Send className="h-5 w-5 text-red-600 dark:text-red-400" /></div>
                  <div className="font-semibold">Report to Authority</div>
                </div>
                <p className="text-sm text-muted-foreground">This breach will be reported to the supervisory authority</p>
              </button>

              <button type="button" data-testid="dpo-decision-no-report"
                onClick={() => !isMandatory && updateField('dpo_reporting_decision', 'no_report')}
                disabled={isMandatory}
                title={isMandatory ? `Notification is mandatory for all breaches under ${rules?.legalBasis}` : undefined}
                className={cn("p-4 border-2 rounded-xl text-left transition-all relative",
                  isMandatory && "opacity-40 cursor-not-allowed",
                  !isMandatory && formData.dpo_reporting_decision === 'no_report' ? "border-green-500 bg-green-50 dark:bg-green-950/30 ring-2 ring-green-500" : !isMandatory ? "border-border hover:border-green-300" : "border-border")}>
                {formData.dpo_reporting_decision === 'no_report' && <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-green-500" />}
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"><FileText className="h-5 w-5 text-green-600 dark:text-green-400" /></div>
                  <div className="font-semibold">Internal Record Only</div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isMandatory ? `Not available — notification is mandatory under ${rules?.legalBasis}` : 'Document internally without notifying the authority'}
                </p>
              </button>

              <button type="button" data-testid="dpo-decision-not-breach"
                onClick={() => !isMandatory && updateField('dpo_reporting_decision', 'not_a_breach')}
                disabled={isMandatory}
                title={isMandatory ? `Notification is mandatory for all breaches under ${rules?.legalBasis}` : undefined}
                className={cn("p-4 border-2 rounded-xl text-left transition-all relative",
                  isMandatory && "opacity-40 cursor-not-allowed",
                  !isMandatory && formData.dpo_reporting_decision === 'not_a_breach' ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 ring-2 ring-amber-500" : !isMandatory ? "border-border hover:border-amber-300" : "border-border")}>
                {formData.dpo_reporting_decision === 'not_a_breach' && <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-amber-500" />}
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center"><Shield className="h-5 w-5 text-amber-600 dark:text-amber-400" /></div>
                  <div className="font-semibold">Not a Breach</div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isMandatory ? `Not available — notification is mandatory under ${rules?.legalBasis}` : 'DPO has determined this is not a personal data breach'}
                </p>
              </button>
            </div>
          ) : (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex gap-3">
                <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-medium">Notification Decision Reserved for DPO</p>
                  <p className="mt-1">The notification decision is reserved for your DPO. Your DPO has been notified of this incident and will complete this step.</p>
                  {formData.dpo_reporting_decision && (
                    <p className="mt-3 font-medium text-blue-800 dark:text-blue-200">
                      Current Decision: <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 ml-1">
                        {formData.dpo_reporting_decision === 'report' && 'Report to Authority'}
                        {formData.dpo_reporting_decision === 'no_report' && 'Internal Record Only'}
                        {formData.dpo_reporting_decision === 'not_a_breach' && 'Not a Breach'}
                        {formData.dpo_reporting_decision === 'pending' && 'Pending'}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isDpoRole && ((formData.dpo_reporting_decision === 'no_report') ||
           (formData.dpo_reporting_decision === 'report' && requirement?.mustReport === false) ||
           (formData.dpo_reporting_decision === 'not_a_breach') ||
           requirement?.mustReport === null) ? (
            <div className="pt-2">
              <Label>
                Reason <span className="text-destructive">*</span>
                {formData.dpo_reporting_decision === 'no_report' && <span className="text-xs text-muted-foreground ml-2">(Required — mandatory for internal record only decisions)</span>}
                {requirement?.mustReport === null && formData.dpo_reporting_decision !== 'no_report' && <span className="text-xs text-muted-foreground ml-2">(Required - DPO discretion applies)</span>}
                {requirement?.mustReport !== null && formData.dpo_reporting_decision && formData.dpo_reporting_decision !== 'no_report' && <span className="text-xs text-amber-600 ml-2">(Differs from system recommendation)</span>}
              </Label>
              <Textarea data-testid="dpo-justification" value={formData.dpo_reporting_justification || ''}
                onChange={e => {
                  updateField('dpo_reporting_justification', e.target.value);
                  if (formData.dpo_reporting_decision === 'no_report') {
                    updateField('notification_reason', e.target.value);
                  }
                }}
                placeholder={formData.dpo_reporting_decision === 'no_report' ? 'Enter reason for not notifying the authority...' : 'Explain your decision...'}
                className="mt-1.5" rows={3} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Notification Details */}
      {formData.dpo_reporting_decision === 'report' && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Notification Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Supervisory Authority</Label>
                <Select value={formData.supervisory_authority || ''} onValueChange={v => updateField('supervisory_authority', v)}>
                  <SelectTrigger data-testid="supervisory-authority" className="mt-1.5"><SelectValue placeholder="Select authority" /></SelectTrigger>
                  <SelectContent>{SUPERVISORY_AUTHORITIES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Confirmed Deadline <span className="text-destructive">*</span>
                  {suggestedDeadlineDate && <span className="text-xs text-muted-foreground ml-2">(Suggested: {new Date(suggestedDeadlineDate).toLocaleDateString()})</span>}
                </Label>
                <div className="mt-1.5"><DatePicker data-testid="confirmed-deadline" value={formData.confirmed_deadline || suggestedDeadlineDate || ''} onChange={(date) => updateField('confirmed_deadline', date)} placeholder="Confirm notification deadline" /></div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Date Notified</Label><div className="mt-1.5"><DatePicker data-testid="date-notified" value={formData.date_notified || ''} onChange={(date) => updateField('date_notified', date)} placeholder="Select notification date" /></div></div>
              <div><Label>Authority Reference Number</Label><Input data-testid="reference-number" value={formData.reference_number || ''} onChange={e => updateField('reference_number', e.target.value)} placeholder="Reference from authority" className="mt-1.5" /></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Subject Notification */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Data Subject Notification</CardTitle>
          <CardDescription>
            {isMandatory
              ? 'Required for all breaches — affected individuals must be notified'
              : (rules?.subjectNotification || 'Determine whether affected individuals need to be notified')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button type="button" data-testid="subject-notification-required"
              onClick={() => { updateField('subject_notification', 'required'); updateField('subject_notification_required', true); }}
              className={cn("p-4 border-2 rounded-xl text-left transition-all relative",
                formData.subject_notification === 'required' ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/50")}>
              {formData.subject_notification === 'required' && <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-primary" />}
              <div className="flex items-center gap-3 mb-2"><Users className="h-5 w-5 text-primary" /><div className="font-semibold">Required</div></div>
              <p className="text-sm text-muted-foreground">
                {isMandatory
                  ? `Mandatory under ${rules?.legalBasis} regardless of risk level`
                  : 'Breach likely to result in high risk to data subjects'}
              </p>
            </button>
            <button type="button" data-testid="subject-notification-not-required"
              onClick={() => { updateField('subject_notification', 'not_required'); updateField('subject_notification_required', false); }}
              className={cn("p-4 border-2 rounded-xl text-left transition-all relative",
                formData.subject_notification === 'not_required' ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/50")}>
              {formData.subject_notification === 'not_required' && <CheckCircle2 className="absolute top-3 right-3 h-5 w-5 text-primary" />}
              <div className="flex items-center gap-3 mb-2"><Shield className="h-5 w-5 text-primary" /><div className="font-semibold">Not Required</div></div>
              <p className="text-sm text-muted-foreground">Low risk or adequate safeguards in place</p>
            </button>
          </div>
          {formData.subject_notification === 'required' && (
            <div><Label>Communication Method</Label><Input data-testid="communication-method" value={formData.communication_method || ''} onChange={e => updateField('communication_method', e.target.value)} placeholder="e.g., Email, Letter, Website Notice" className="mt-1.5" /></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
